import { completedDowntimeDurationMs, isShortProductionDowntime } from "../../../../shared/production/downtime";
import type { OperatorProductPackaging } from "../case-packaging";
import { caseQuantity } from "../case-quantity";
import { averageRunRatePerMinute, machineCountLabel, plannedBoxCount, type LiveCounter } from "../live-counter";
import type { stableTelemetryPresentation } from "../live-telemetry-state";
import type { ProductionReferenceData, ProductionRun } from "../models";
import { operatorCaseProgress } from "../operator-case-progress";
import type { CompletedCasesPresentation } from "../operator-completed-cases";
import { operatorRunLosses, operatorRunQuantities } from "../operator-operational-summary";
import { operatorProductNote } from "../operator-product-note";
import { operatorRunTime } from "../operator-run-time";
import { operatorAmount } from "./quantity-format";

type OperatorProductionViewInput = {
  selected: ProductionRun | null;
  totals: { PRODUCTION: number | null; WASTE: number | null; SCRAP: number | null };
  packaging: OperatorProductPackaging | null;
  workOrders: ProductionReferenceData["workOrders"];
  liveCounter: {
    counter: LiveCounter | null;
    telemetry: Pick<ReturnType<typeof stableTelemetryPresentation>, "counter" | "count" | "countRetained" | "speed">;
    completedCases: CompletedCasesPresentation;
  };
  openDowntime: ProductionRun["downtimes"][number] | null;
  /** Actual run clock, frozen at closedAt for completed jobs. */
  runNow: number;
  /** Current wall clock for forecasts and local PLC baseline age. */
  now: number;
};

export function operatorProductionViewModel({ selected, totals, packaging, workOrders, liveCounter, openDowntime, runNow, now }: OperatorProductionViewInput) {
  const unknownProduction = operatorRunQuantities(selected).unknownProduction > 0;
  const scrapGroups = selected ? operatorRunLosses(selected) : [];
  const scrapLabel = scrapGroups == null ? "Miktar belirsiz" : scrapGroups.length
    ? scrapGroups.map((group) => operatorAmount(group.quantity, group.unit)).join(" + ")
    : "Kayıt yok";
  const shortStops = selected?.downtimes.filter(isShortProductionDowntime) ?? [];
  const shortStopMilliseconds = selected?.terminalSummary?.metrics.timing.shortStopMilliseconds
    ?? shortStops.reduce((total, item) => total + (completedDowntimeDurationMs(item) ?? 0), 0);
  // Dashboard values use the display-only stable snapshot. Raw liveCounter.counter
  // remains the authority for machine state and every control above/below this view.
  const dashboardCounter = liveCounter.telemetry.counter;
  const machineConfiguredForDisplay = (dashboardCounter?.configured ?? liveCounter.counter?.configured) === true;
  const usesCompletedCases = machineConfiguredForDisplay && /^(KOLİ|KOLI)$/u.test(selected?.unit.trim().toLocaleUpperCase("tr-TR") ?? "");
  const machineBoxes = liveCounter.telemetry.count;
  const caseProgress = operatorCaseProgress({
    lineName: selected?.lineName,
    primaryUnit: selected?.unit ?? "",
    plannedQuantity: selected?.plannedQuantity ?? null,
    producedQuantity: unknownProduction || dashboardCounter?.source === "LOCAL_PLC_TEST" ? null : totals.PRODUCTION,
    comparisonProducedQuantity: liveCounter.telemetry.countRetained || liveCounter.counter?.runQuantity !== machineBoxes || liveCounter.counter?.source === "LOCAL_PLC_TEST"
      ? null : liveCounter.counter?.persistedProductionQuantity ?? null,
    packaging, packagingUnit: packaging?.unit,
    // Current product content does not rebind an existing run's frozen ledger.
    countedQuantity: dashboardCounter?.packaging?.boxesPerCase === packaging?.boxesPerCase ? machineBoxes : null,
    countedUnit: dashboardCounter?.unit,
    counterQuality: dashboardCounter?.quality,
    completedCases: usesCompletedCases ? liveCounter.completedCases : undefined,
  });
  const machinePlannedBoxes = plannedBoxCount(dashboardCounter, selected?.plannedQuantity ?? null, selected?.unit ?? "");
  const totalPlannedCases=caseQuantity(selected?.plannedQuantity,selected?.unit ?? "",dashboardCounter?.packaging?.boxesPerCase);
  const machineComparison = dashboardCounter?.configured && machinePlannedBoxes != null && machinePlannedBoxes > 0 &&
    (dashboardCounter.source === "LOCAL_PLC_TEST" || dashboardCounter.unit?.toLocaleUpperCase("tr-TR") === "KUTU");
  const machineUnit = machineCountLabel(selected?.lineName ?? "");
  const chartProduced = machineComparison ? machineBoxes : dashboardCounter?.source === "LOCAL_PLC_TEST" || unknownProduction ? null : totals.PRODUCTION;
  const chartUnit = machineComparison ? machineUnit : selected?.unit ?? "";
  const remainingQuantity =
    usesCompletedCases ? caseProgress.remaining : selected?.plannedQuantity == null || totals.PRODUCTION == null
      ? null
      : Math.max(0, selected.plannedQuantity - totals.PRODUCTION);
  // Operational time starts with the actual run, independent of Excel hours.
  const timeline = selected ? operatorRunTime(selected, runNow) : null;
  const livePace = (() => {
    if (!timeline || !selected?.plannedQuantity || selected.plannedQuantity <= 0) return null;
    const productiveMilliseconds = timeline.productionMilliseconds;
    if (usesCompletedCases) {
      if (liveCounter.completedCases.source !== "RUN" || liveCounter.completedCases.retained ||
          caseProgress.actual == null || caseProgress.actual <= 0 || productiveMilliseconds < 60_000) return null;
      return { productiveMilliseconds, actualRatePerHour: caseProgress.actual * 3_600_000 / productiveMilliseconds };
    }
    if (totals.PRODUCTION == null || totals.WASTE == null || totals.SCRAP == null) return null;
    const recordedQuantity = totals.PRODUCTION + totals.WASTE + totals.SCRAP;
    if (productiveMilliseconds < 60_000 || recordedQuantity <= 0) return null;
    return {
      productiveMilliseconds,
      actualRatePerHour: totals.PRODUCTION * 3_600_000 / productiveMilliseconds,
    };
  })();
  const projectedFinishAt =
    selected?.status === "ACTIVE" &&
    !openDowntime &&
    !(usesCompletedCases ? liveCounter.completedCases.retained : machineComparison && liveCounter.telemetry.countRetained) &&
    livePace &&
    livePace.productiveMilliseconds >= 30 * 60_000 &&
    livePace.actualRatePerHour > 0 &&
    remainingQuantity != null
      ? new Date(now + (remainingQuantity / livePace.actualRatePerHour) * 3_600_000).toISOString()
      : null;
  const observedMilliseconds = dashboardCounter?.source === "LOCAL_PLC_TEST"
    ? now - Date.parse(dashboardCounter.baselineAt ?? "")
    : timeline?.totalMilliseconds ?? null;
  const usesMachineSpeed = dashboardCounter?.configured === true;
  const speedUnit = usesMachineSpeed
    ? dashboardCounter?.source === "LOCAL_PLC_TEST" || dashboardCounter?.unit?.toLocaleUpperCase("tr-TR") === "KUTU"
      ? machineUnit : dashboardCounter?.unit ?? "birim"
    : chartUnit;
  const actualRatePerMinute = usesMachineSpeed ? liveCounter.telemetry.speed : averageRunRatePerMinute(chartProduced, observedMilliseconds);
  const productNote = operatorProductNote(selected, workOrders);
  const selectedPlanNote = productNote?.note ?? null;
  return {
    machineConfiguredForDisplay, usesCompletedCases, machineBoxes, caseProgress,
    totalPlannedCases, machineComparison, machineUnit, timeline, projectedFinishAt,
    usesMachineSpeed, speedUnit, actualRatePerMinute, productNote, selectedPlanNote,
    scrapGroups, scrapLabel, shortStopMilliseconds,
  };
}
